import { Typography } from "@mui/material";
import { GeoJSONGeometry } from "wellknown";

type GeometryInfoProps = {
  endpoint: { tableName: string } | null;
  selectedCoordinates?: [number, number];
  selectedPolygon?: any;
  geometry?: string;
};

// Helper functions for geometry display
const getGeometryDisplayName = (geometry: GeoJSONGeometry | any): string => {
  return geometry?.type || "Geometry";
};

const getGeometryDescription = (geometry: GeoJSONGeometry | any): string => {
  if (!geometry?.type || !geometry?.coordinates) return "defined";

  switch (geometry.type) {
    case "MultiPolygon":
      const totalVertices = geometry.coordinates.reduce((total: number, poly: number[][][]) => {
        return total + (poly[0]?.length || 0); // Count vertices in each polygon's outer ring
      }, 0);
      return `${geometry.coordinates.length} polygons, ${totalVertices} total vertices`;

    case "Polygon":
      return `${geometry.coordinates[0]?.length || 0} vertices`;

    case "LineString":
      return `${geometry.coordinates.length} points`;

    case "MultiLineString":
      const totalPoints = geometry.coordinates.reduce((total: number, line: number[][]) => {
        return total + (line?.length || 0);
      }, 0);
      return `${geometry.coordinates.length} lines, ${totalPoints} total points`;

    case "Point":
      return `coordinates: ${geometry.coordinates[1]?.toFixed(6)}, ${geometry.coordinates[0]?.toFixed(6)}`;

    case "MultiPoint":
      return `${geometry.coordinates.length} points`;

    default:
      return "defined";
  }
};

export default function GeometryInfo({ endpoint, selectedCoordinates, selectedPolygon, geometry }: GeometryInfoProps) {
  if (!endpoint) return null;

  let content: React.ReactNode = null;

  if (endpoint.tableName === "places" && selectedCoordinates) {
    content = `${selectedCoordinates[1].toFixed(6)}, ${selectedCoordinates[0].toFixed(6)}`;
  } else if (endpoint.tableName === "family-areas" && selectedPolygon) {
    content = (
      <>
        <strong>{getGeometryDisplayName(selectedPolygon)}:</strong> {getGeometryDescription(selectedPolygon)}
      </>
    );
  } else if (endpoint.tableName === "trips" && selectedPolygon) {
    content = (
      <>
        <strong>Route ({getGeometryDisplayName(selectedPolygon)}):</strong> {getGeometryDescription(selectedPolygon)}
      </>
    );
  } else if (geometry) {
    let label = "Custom geometry";
    try {
      const geom = JSON.parse(geometry);
      label = `${geom.type} defined`;
    } catch {}

    content = (
      <>
        <strong>Geometry:</strong> {label}
      </>
    );
  }

  return (
    <Typography variant={"body1"} color={content ? "grey.500" : "grey.400"}>
      {content ||
        (endpoint.tableName === "places"
          ? "Click on map to set location or edit GeoJSON below"
          : "Edit GeoJSON below or use external tools")}
    </Typography>
  );
}
